import React, { FC, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { DispatchSetAction } from '@sistemiv/s-components/dist/esm/types/types'
import { Button, Input, ModalDialog } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'

type AddFolderOrRenameModalProps = {
  open: boolean
  creating?: boolean
  setOpen: DispatchSetAction<boolean>
  onCancel?: () => void
  onCreate?: (name: string) => void
  mode: 'create' | 'edit'
  initialValue?: string
  title?: string
}

const AddFolderOrRenameModal: FC<AddFolderOrRenameModalProps> = ({
  open,
  creating,
  setOpen,
  onCancel,
  onCreate,
  mode,
  initialValue,
  title,
}) => {
  const [value, setValue] = useState(initialValue ? initialValue : '')
  const { t } = useTranslation()
  const [error, setError] = useState('')

  const validate = (inputValue: string): string => {
    if (
      mode === 'edit' &&
      initialValue?.includes('.') &&
      (!inputValue.includes('.') || !/^[^<>:"/\\|?*]+$/.test(inputValue))
    ) {
      return 'Naziv fajla mora da sadrzi eksenziju fajla i ne sme da sadrzi karaktere < > : " / \\ | ? *'
    }

    if (
      (mode === 'create' || (mode === 'edit' && !initialValue?.includes('.'))) &&
      !/^[^<>:"/\\|?*.]+$/.test(inputValue)
    ) {
      return 'Unos ne sme sadržati sledeće karaktere: < > : " / \\ | ? * .'
    }
    return '' // nema greške
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    const validationError = validate(newValue)
    setError(validationError)
  }

  return (
    <ModalDialog isOpen={open} setOpen={setOpen} className='!max-w-md'>
      <Dialog.Title as='h3' className='leading-6 text-lg flex justify-between'>
        {title ? title : mode === 'create' ? t('newFolder') : t('renameFolder')}
        <button onClick={() => setOpen(false)}>
          <XMarkIcon className='w-5 h-5 stroke-gray-500' />
        </button>
      </Dialog.Title>
      <div>
        <div className='mt-5 space-y-6'>
          <Input
            id='name'
            placeholder={t('name') as string}
            type='text'
            removePencil
            classes='!border-solid border border-gray-300 focus:border-sky-100'
            value={value}
            onChange={handleChange}
          />
        </div>
        {error && <p className='text-red-600'>{error}</p>}
        <div className='flex items-center justify-end gap-x-3 mt-6'>
          <Button className='hover:bg-gray-100 active:text-blue-500' onClick={() => onCancel?.()}>
            {t('cancel')}
          </Button>
          <Button
            className='text-white bg-blue-500 disabled:bg-slate-200 disabled:text-slate-400'
            onClick={() => onCreate?.(value)}
            disabled={!value || initialValue === value || !!error}
            loading={creating}
          >
            {mode === 'create' ? t('create') : t('rename')}
          </Button>
        </div>
      </div>
    </ModalDialog>
  )
}

export default AddFolderOrRenameModal
